import React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Settings, FileText, Briefcase, Upload, FileCheck, Download, Calendar, Plus } from 'lucide-react';
import { AdminCard } from '../../components/admin/AdminCard';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  last_ip_address: string | null;
  last_login_at: string | null;
  login_count: number;
  avatar_url: string | null;
  is_admin: boolean;
  created_at: string;
  last_active: string;
}

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  type: string;
  capacity: number;
  created_at: string;
}


interface ResumeReview {
  id: string;
  status: 'pending' | 'reviewed';
  created_at: string;
  resume_url: string;
  email: string;
  user_profiles: {
    first_name: string;
    last_name: string;
  };
}

// Add helper function at the top of the file after interfaces
function getDisplayName(review: ResumeReview): string {
  const firstName = review.user_profiles?.first_name?.trim();
  const lastName = review.user_profiles?.last_name?.trim();
  
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }
  
  if (review.email) {
    return review.email;
  }
  
  return 'Anonymous User';
}

// Add helper function for comment author names
function getCommentAuthorName(comment: any): string {
  if (!comment.user_profiles) return 'Unknown User';
  
  const firstName = comment.user_profiles?.first_name?.trim() || '';
  const lastName = comment.user_profiles?.last_name?.trim() || '';
  
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }
  
  return 'Unknown User';
}

const adminCards = [
  {
    title: 'Courses',
    description: 'Manage course content and structure',
    icon: <BookOpen className="h-6 w-6 text-white" />,
    path: '/admin/courses',
    color: 'bg-indigo-600'
  },
  {
    title: 'Jobs',
    description: 'Manage job board listings',
    icon: <Briefcase className="h-6 w-6 text-white" />,
    path: '/admin/jobs/manage',
    color: 'bg-emerald-600'
  },
  {
    title: 'Blog',
    description: 'Manage blog posts and content',
    icon: <FileText className="h-6 w-6 text-white" />,
    path: '/admin/blog',
    color: 'bg-purple-600'
  },
  {
    title: 'Resume Reviews',
    description: 'Review and provide feedback on resumes',
    icon: <FileText className="h-6 w-6 text-white" />,
    path: '/admin/resume-review',
    color: 'bg-pink-600'
  },
  {
    title: 'Settings',
    description: 'Platform configuration',
    icon: <Settings className="h-6 w-6 text-white" />,
    path: '/admin/settings',
    color: 'bg-gray-600'
  }
];

export function AdminDashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [recentReviews, setRecentReviews] = useState<ResumeReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    type: 'networking',
    capacity: 50,
    image_url: ''
  });
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<ResumeReview | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadUsers();
    loadRecentReviews();
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('date', { ascending: true })
        .limit(5);

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load events');
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('events')
        .insert([eventFormData]);

      if (error) throw error;
      
      toast.success('Event created successfully');
      setShowEventForm(false);
      setEventFormData({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        type: 'networking',
        capacity: 50,
        image_url: ''
      });
      loadEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          id,
          first_name,
          last_name,
          avatar_url,
          is_admin,
          last_login_at,
          login_count,
          created_at,
          updated_at
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to ensure no null values
      const safeUsers = (data || []).map(user => ({
        id: user?.id || '',
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: '', // Email will be empty since we can't get it directly
        avatar_url: user?.avatar_url || null,
        is_admin: Boolean(user?.is_admin),
        last_ip_address: user?.last_ip_address || null,
        last_login_at: user?.last_login_at || null,
        login_count: user?.login_count || 0,
        created_at: user?.created_at || new Date().toISOString(),
        last_active: user?.last_login_at || user?.created_at || new Date().toISOString()
      }));

      setUsers(safeUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
      setUsers([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedReview) {
      loadComments(selectedReview.id);
    } else {
      setComments([]);
    }
  }, [selectedReview]);

  const loadComments = async (reviewId: string) => {
    setIsLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('resume_review_comments')
        .select(`
          id,
          content,
          created_at,
          user_id,
          user_profiles:user_id (
            first_name,
            last_name,
            is_admin
          )
        `)
        .eq('review_id', reviewId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Failed to load comments');
      setComments([]); // Set empty array on error
    } finally {
      setIsLoadingComments(false);
    }
  };
  const loadRecentReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('resume_reviews')
        .select('*, user_profiles(*)')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      
      // Transform data to ensure proper display names
      const safeReviews = (data || []).map(review => ({
        ...review,
        user_profiles: {
          first_name: review.user_profiles?.first_name || '',
          last_name: review.user_profiles?.last_name || ''
        }
      }));
      
      setRecentReviews(safeReviews);
    } catch (error) {
      console.error('Error loading recent reviews:', error);
      toast.error('Failed to load recent reviews');
      setRecentReviews([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const csvText = event.target?.result as string;
        const lines = csvText.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

        // Validate required columns
        const requiredColumns = ['company', 'date', 'location', 'employees_affected'];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
          return;
        }

        // Process records
        const newRecords = lines.slice(1)
          .filter(line => line.trim())
          .map(line => {
            const values = line.split(',').map(v => v.trim());
            const record: any = {};
            headers.forEach((header, index) => {
              record[header] = values[index];
            });
            return record;
          });

        // Insert records into database
        const { error } = await supabase
          .from('layoff_records')
          .insert(newRecords);

        if (error) throw error;

        toast.success('Layoff records imported successfully');
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to import records');
    } finally {
      if (e.target) e.target.value = ''; // Reset file input
    }
  };

  const handleDownload = async (review: ResumeReview) => {
    try {
      setIsDownloading(review.id);
      
      // Create safe filename
      const fileName = `resume-${review.user_profiles.first_name || 'user'}-${review.user_profiles.last_name || Date.now()}.pdf`;
      
      const response = await fetch(review.resume_url);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Resume downloaded successfully');
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error('Failed to download resume');
    } finally {
      setIsDownloading(null);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReview || !newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      const { error } = await supabase
        .from('resume_review_comments')
        .insert({
          review_id: selectedReview.id,
          user_id: user?.id,
          content: newComment.trim()
        });

      if (error) throw error;

      // Reload comments
      await loadComments(selectedReview.id);
      setNewComment(''); // Clear input
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-black mb-2">Admin Dashboard</h1>
        <div className="flex justify-between items-center">
          <p className="text-black">Manage your platform settings and content</p>
          <div>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="layoff-csv-upload"
            />
            <label
              htmlFor="layoff-csv-upload"
              className="inline-flex items-center px-4 py-2 bg-[#373737] text-white rounded-xl hover:opacity-90 transition-colors cursor-pointer"
            >
              <Upload className="h-5 w-5 mr-2" />
              Import Layoff Data
            </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {adminCards.map((card) => (
          <AdminCard key={card.path} {...card} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-background-card rounded-xl p-6 border border-gray-800">
          <div className="flex items-center space-x-2 mb-6">
            <FileCheck className="h-5 w-5 text-accent-green" />
            <h2 className="text-lg font-bold text-black">Recent Resume Reviews</h2>
          </div>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : recentReviews.length === 0 ? (
            <p className="text-center text-gray-500">No resume reviews yet</p>
          ) : (
            <div className="space-y-4">
              {recentReviews.map((review) => (
                <div key={review.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="flex items-center">
                      <button
                        onClick={() => setSelectedReview(review)}
                        className="font-medium text-black hover:text-[#800020] transition-colors"
                      >
                        {getDisplayName(review)}
                      </button>
                      <button
                        onClick={() => handleDownload(review)}
                        className={`ml-3 inline-flex items-center text-[#800020] hover:opacity-80 text-sm ${
                          isDownloading === review.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Download className={`h-4 w-4 mr-1 ${
                          isDownloading === review.id ? 'animate-spin' : ''
                        }`} />
                        {isDownloading === review.id ? 'Downloading...' : 'Download'}
                      </button>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {new Date(review.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center">
                    {review.status === 'pending' ? (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                        Pending
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        Reviewed
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User Details Modal */}
        {selectedReview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-black">
                    User Details
                  </h2>
                  <button 
                    onClick={() => setSelectedReview(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Name</h3>
                    <p className="mt-1 text-black">
                      {getDisplayName(selectedReview)}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Email</h3>
                    <p className="mt-1 text-black">{selectedReview.email}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Submitted</h3>
                    <p className="mt-1 text-black">
                      {new Date(selectedReview.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Comments</h3>
                    {isLoadingComments ? (
                      <div className="flex justify-center py-4">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : comments.length > 0 ? (
                      <div className="space-y-4">
                        {comments.map((comment) => (
                          <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                <span className="font-medium text-black">
                                  {comment.user_id === user?.id ? 'You' : getCommentAuthorName(comment)}
                                </span>
                                {comment.user_profiles?.is_admin && (
                                  <span className="ml-2 px-2 py-0.5 bg-[#800020] text-white text-xs rounded-full">
                                    Admin
                                  </span>
                                )}
                              </div>
                              <span className="text-sm text-gray-500">
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-black">{comment.content}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No comments yet</p>
                    )}
                  </div>
                  
                  {/* Comment Form */}
                  <form onSubmit={handleSubmitComment} className="mt-4">
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      Add Comment
                    </label>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
                      rows={3}
                      placeholder="Enter your comment..."
                      required
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmittingComment || !newComment.trim()}
                        className="px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                      >
                        {isSubmittingComment ? 'Submitting...' : 'Submit Comment'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-background-card rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-black">Manage Events</h3>
              <button
                onClick={() => setShowEventForm(true)}
                className="px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </button>
            </div>
            
            {events.length > 0 ? (
              <div className="space-y-4">
                {events.map((event) => (
                  <div key={event.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-black">{event.title}</h4>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-sm text-gray-500">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            {new Date(event.date).toLocaleDateString()} at {event.time}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            event.type === 'networking'
                              ? 'bg-blue-100 text-blue-800'
                              : event.type === 'workshop'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {event.type}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // Handle edit event
                        }}
                        className="text-[#800020] hover:opacity-80"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500">No events scheduled</p>
            )}

            {/* Event Creation Form Modal */}
            {showEventForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl max-w-2xl w-full">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-black">Create New Event</h2>
                  </div>
                  <form onSubmit={handleCreateEvent} className="p-6 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-black mb-2">
                        Title*
                      </label>
                      <input
                        type="text"
                        value={eventFormData.title}
                        onChange={e => setEventFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-black mb-2">
                        Description
                      </label>
                      <textarea
                        value={eventFormData.description}
                        onChange={e => setEventFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-black mb-2">
                          Date*
                        </label>
                        <input
                          type="date"
                          value={eventFormData.date}
                          onChange={e => setEventFormData(prev => ({ ...prev, date: e.target.value }))}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-black mb-2">
                          Time*
                        </label>
                        <input
                          type="time"
                          value={eventFormData.time}
                          onChange={e => setEventFormData(prev => ({ ...prev, time: e.target.value }))}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-black mb-2">
                        Location*
                      </label>
                      <input
                        type="text"
                        value={eventFormData.location}
                        onChange={e => setEventFormData(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-black mb-2">
                          Type*
                        </label>
                        <select
                          value={eventFormData.type}
                          onChange={e => setEventFormData(prev => ({ ...prev, type: e.target.value }))}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                          required
                        >
                          <option value="networking">Networking</option>
                          <option value="workshop">Workshop</option>
                          <option value="webinar">Webinar</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-black mb-2">
                          Capacity*
                        </label>
                        <input
                          type="number"
                          value={eventFormData.capacity}
                          onChange={e => setEventFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                          min="1"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-black mb-2">
                        Image URL
                      </label>
                      <input
                        type="url"
                        value={eventFormData.image_url}
                        onChange={e => setEventFormData(prev => ({ ...prev, image_url: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-black"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowEventForm(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors"
                      >
                        Create Event
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>

          <div className="bg-background-card rounded-xl p-6 border border-gray-800">
            <h3 className="text-lg font-bold text-black mb-4">Recent Users</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-4 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : !users || users.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-4 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.slice(0, 5).map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <img
                              src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent((user.first_name || '') + ' ' + (user.last_name || ''))}`}
                              alt="User avatar"
                              className="h-8 w-8 rounded-full"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-black">
                                {user.first_name || user.last_name 
                                  ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                  : 'Unnamed User'}
                              </div>
                              <div className="text-sm text-gray-500">
                                <div>Last login: {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}</div>
                                <div>IP: {user.last_ip_address || 'Unknown'}</div>
                                <div>Login count: {user.login_count || 0}</div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            user.is_admin
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.is_admin ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/admin/users/${user.id}`}
                            className="text-[#800020] hover:opacity-80"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="mt-4 text-center">
                <Link
                  to="/admin/users"
                  className="inline-flex items-center px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors text-sm"
                >
                  View All Users
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


interface ActivityItemProps {
  title: string;
  description: string;
  time: string;
}

function ActivityItem({ title, description, time }: ActivityItemProps) {
  return (
    <div className="border-b border-gray-800 last:border-0 pb-4 last:pb-0">
      <h4 className="text-black font-medium">{title}</h4>
      <p className="text-black text-sm mt-1">{description}</p>
      <span className="text-xs text-gray-500 mt-1">{time}</span>
    </div>
  );
}